import { useState, useEffect } from "react"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import styled from "styled-components"
import Navbar from "./Navbar"
import LoginPanel from "./LoginPanel"
import PopularTags from "./PopularTags"
import RecommendedPosts from "./RecommendedPosts"
import PeopleYouMayKnow from "./PeopleYouMayKnow"
import { useTheme } from "../context/ThemeContext"
import { useAuth } from "../context/AuthContext"
import { MessageCircle, Users, X, Send, ExternalLink } from "lucide-react"
import DirectMessagesPage from "../pages/DirectMessagesPage"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: ${props => props.theme.backgroundColor};
  justify-content: center;
  transition: background-color 0.3s ease;
`

const Content = styled.div`
  display: flex;
  flex: 1;
  padding: 20px;
  gap: 20px;
  align-self: center;
  width: 100%;
  max-width: 1280px;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 10px;
    gap: 15px;
    
    /* 홈 페이지에서만 사이드바를 메인 콘텐츠 아래로 */
    &.home-layout {
      .mobile-sidebar {
        order: 2;
      }
      
      .mobile-main {
        order: 1;
      }
    }
  }
  
  @media (max-width: 480px) {
    padding: 8px;
    gap: 12px;
  }
  
  @media (max-width: 350px) {
    padding: 6px;
    gap: 10px;
  }
`

const MainContent = styled.main`

  flex: 1;
`

const Sidebar = styled.aside`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    width: 100%;
  }
`


const DesktopLoginSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  @media (max-width: 768px) {
    display: none;
  }
`

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  @media (max-width: 768px) {
    margin-top: 20px;
  }
`

const MobileLoginPanel = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 400px;
    background-color: ${props => props.theme.cardBackground};
    border-radius: 12px;
    box-shadow: ${props => props.theme.shadowHover};
    z-index: 1100;
    animation: fadeIn 0.2s ease-out;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.9);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
`

const MobileLoginBackdrop = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1099;
  }
`

const ToggleButton = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${props => props.theme.buttonPrimary};
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: ${props => props.theme.shadowHover};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.buttonPrimaryHover};
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
  }
  
  @media (max-width: 350px) {
    width: 46px;
    height: 46px;
  }
`

const MobileMessagePanel = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'flex' : 'none'};
    flex-direction: column;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background-color: ${props => props.theme.cardBackground};
    z-index: 9999;
    animation: ${props => props.$isOpen ? 'slideInFromRight' : 'slideOutToRight'} 0.3s ease-out;
    overflow: hidden;
  }
  
  @media (min-width: 769px) {
    display: none !important;
  }
  
  @keyframes slideInFromRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOutToRight {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
`

const MessagePanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${props => props.theme.border};
  min-height: 60px;
  background-color: ${props => props.theme.cardBackground};
  
  @media (max-width: 480px) {
    padding: 14px 16px;
    min-height: 56px;
  }
  
  @media (max-width: 350px) {
    padding: 12px 14px;
    min-height: 52px;
  }
`

const MessagePanelTitle = styled.h3`
  margin: 0;
  color: ${props => props.theme.textPrimary};
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 480px) {
    font-size: 16px;
    gap: 6px;
  }
  
  @media (max-width: 350px) {
    font-size: 15px;
    gap: 5px;
  }
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
  
  &:hover {
    color: ${props => props.theme.textPrimary};
    background-color: ${props => props.theme.hoverBackground};
  }
`

const ConversationsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
`

const ConversationItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 20px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
  
  @media (max-width: 480px) {
    padding: 10px 16px;
  }
  
  @media (max-width: 350px) {
    padding: 8px 14px;
  }
`

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.theme.border};
  background-image: ${props => props.$avatar ? `url(${props.$avatar})` : 'none'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
`

const ConversationInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const ConversationName = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.theme.textPrimary};
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const LastMessage = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const EmptyMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: ${props => props.theme.textSecondary};
  
  .icon {
    margin-bottom: 12px;
    color: ${props => props.theme.textMuted};
  }
  
  h4 {
    margin: 0 0 8px 0;
    color: ${props => props.theme.textPrimary};
    font-size: 16px;
  }
  
  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.4;
  }
`

const ViewAllButton = styled.button`
  width: 100%;
  padding: 12px 20px;
  background: none;
  border: none;
  border-top: 1px solid ${props => props.theme.border};
  color: ${props => props.theme.buttonPrimary};
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
`

const FloatingTalkButton = styled.a`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FF6B35, #FF8C42);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(255, 107, 53, 0.4);
  cursor: pointer;
  text-decoration: none;
  z-index: 1000;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 24px rgba(255, 107, 53, 0.5);
  }
  
  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    bottom: 16px;
    right: 16px;
    width: 50px;
    height: 50px;
  }
`

const FloatingTalkTooltip = styled.div`
  position: fixed;
  bottom: 88px;
  right: 24px;
  background: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 2px 12px rgba(0,0,0,0.15);
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  z-index: 1000;

  ${FloatingTalkButton}:hover + & {
    opacity: 1;
  }

  @media (max-width: 768px) {
    bottom: 74px;
    right: 16px;
  }
`

export default function MainLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const [activePage, setActivePage] = useState("home")
  const { theme } = useTheme()
  const { isLoggedIn } = useAuth()
  
  // 모바일 메시지 패널 상태
  const [isMobileMessageOpen, setIsMobileMessageOpen] = useState(false)
  
  // 모바일 로그인 패널 상태
  const [isMobileLoginOpen, setIsMobileLoginOpen] = useState(false)
  

  // 로그인 모달 열기 이벤트 리스너
  useEffect(() => {
    const handleShowLoginModal = () => {
      setIsMobileLoginOpen(true);
    };

    window.addEventListener('showLoginModal', handleShowLoginModal);
    return () => window.removeEventListener('showLoginModal', handleShowLoginModal);
  }, []);

  // 현재 경로에 따라 활성 페이지 설정
  useEffect(() => {
    const path = location.pathname.split("/")[1]
    if (path === "") {
      setActivePage("home")
    } else {
      setActivePage(path)
    }
  }, [location])

  // 페이지 변경 핸들러
  const handlePageChange = (page) => {
    setActivePage(page)
    navigate(page === "home" ? "/" : `/${page}`)
  }

  // 모바일 메시지 패널 열기/닫기
  const toggleMobileMessage = () => {
    setIsMobileMessageOpen(!isMobileMessageOpen)
  }
  
  // 모바일 로그인 패널 열기/닫기
  const toggleMobileLogin = () => {
    setIsMobileLoginOpen(!isMobileLoginOpen)
  }
  


  // 시간 포맷팅 함수
  const formatTime = (timestamp) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffHours = (now - date) / (1000 * 60 * 60)
    
    if (diffHours < 24) {
      return date.toLocaleTimeString('ko-KR', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      })
    } else {
      return date.toLocaleDateString('ko-KR', { 
        month: 'short', 
        day: 'numeric' 
      })
    }
  }

  return (
    <Container theme={theme}>
      <Navbar 
        activePage={activePage} 
        setActivePage={handlePageChange}
        onMobileMessageClick={toggleMobileMessage}
        onMobileLoginClick={toggleMobileLogin}
      />
      {activePage === "messages" ? (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <Outlet />
        </div>
      ) : (
        <Content className={activePage === "home" ? "home-layout" : ""}>
          <MainContent className="mobile-main">
            <Outlet />
          </MainContent>
          <Sidebar className="mobile-sidebar">
            <DesktopLoginSection>
              <LoginPanel />
            </DesktopLoginSection>
            <SidebarContent>
              <PopularTags />
              <RecommendedPosts />
              <PeopleYouMayKnow limit={3} />
            </SidebarContent>
          </Sidebar>
        </Content>
      )}
      
      
      {/* 모바일 로그인 패널 */}
      <MobileLoginBackdrop theme={theme} $isOpen={isMobileLoginOpen} onClick={toggleMobileLogin} />
      <MobileLoginPanel theme={theme} $isOpen={isMobileLoginOpen}>
        <LoginPanel />
      </MobileLoginPanel>

      {/* 메시지 페이지 플로팅 버튼 — /messages 경로에선 숨김 */}
      {!location.pathname.startsWith('/messages') && (
        <>
          <FloatingTalkButton
            href="/messages"
            title="메시지로 이동"
          >
            <MessageCircle size={26} fill="white" />
          </FloatingTalkButton>
          <FloatingTalkTooltip theme={theme}>
            💬 메시지
          </FloatingTalkTooltip>
        </>
      )}

      {/* 모바일 메시지 패널 */}
      {isLoggedIn && (
        <MobileMessagePanel theme={theme} $isOpen={isMobileMessageOpen}>
          <MessagePanelHeader theme={theme}>
            <MessagePanelTitle theme={theme}>
              <MessageCircle size={20} />
              메시지
            </MessagePanelTitle>
            <div style={{ display: 'flex', gap: '8px' }}>
              <CloseButton theme={theme} onClick={() => window.location.href = '/messages'} title='매칭하기'>
                <Users size={20} />
              </CloseButton>
              <CloseButton theme={theme} onClick={toggleMobileMessage}>
                <X size={20} />
              </CloseButton>
            </div>
          </MessagePanelHeader>

          <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
            <EmptyMessage theme={theme}>
              <MessageCircle size={48} className="icon" />
              <h4>아직 메시지가 없습니다</h4>
              <p>새로운 대화를 시작해보세요!</p>
            </EmptyMessage>
          </div>
        </MobileMessagePanel>
      )}
    </Container>
  )
}

